// Plan 14-01 Wave 0 — TurnBuilder ephemeral store (Phase A + Phase B selections).
//
// This is the keystone client-side surface for MUI-01/MUI-02. The match
// screen accumulates Phase A and Phase B selections into this atom; the
// Submit Turn CTA reads them and posts a single
// `submitTurn({ phase_a, phase_b })` payload (matching the backend's
// MatchTurnRequest union shape — see
// apps/backend/src/match/validation/word.ts `PhaseABody` and
// apps/backend/src/match/validation/chess.ts `PhaseBBody`).
//
// EPHEMERAL — cleared on TURN_APPLIED (the WS confirmation that the server
// accepted the turn). NOT persisted; a page reload wipes any in-progress
// selection. Persistence is intentionally avoided — DEC-021 (server
// authority) means the only authoritative state is server-projected
// PlayerView; the turn-builder is a one-shot client compose buffer.
//
// Phase A is a discriminant union mirroring the backend byte-for-byte
// (Pattern 1, 14-RESEARCH): PhaseA: 'word' | 'skip' — 2 kinds (Phase 24
// RUNE-05 deleted 'swap'). UNCHANGED by ARCA-619 — the two-step turn keeps
// word → chess; only the chess step (Phase B) became composite.
//
// Phase B (ARCA-619 / ARCA-715) is a COMPOSITE chess step: instead of picking
// exactly one of move / empower / powerup / extend / pass, the player may stage
// ANY combination of an optional move + a role action (empower XOR extend, D2) +
// a power-up, in any order, then Submit once. An all-null composite is the
// do-nothing step that REPLACES the legacy Pass (D4). Mirrors the backend
// `CompositeChessStep` wire shape (apps/backend/src/match/validation/chess.ts
// `normalizeChessStep` — canonical apply order powerup → empower/extend → move,
// D3).
//
// Plans 02-06 (UI + interaction wiring) consume this surface. Plan 02 fills
// the wire-up (setPhaseA from WordTraceLayer); the chess sub-flows
// (MatchScreen) each stage into their slot via stageMove / stageEmpower /
// stageExtend / stagePowerup.

import { atom } from 'nanostores'
import type { PowerUpName, RoleKind } from '../game/PlayerViewSchema'

/**
 * Phase A selection. Mirrors backend `PhaseABody` (word | skip).
 * `null` represents "Phase A not yet selected this turn".
 *
 * Phase 24 (RUNE-05) deleted the legacy `'swap'` discriminator from the
 * live submission union; the Worker route 400-rejects any inbound
 * `kind:'swap'` payload (defense-in-depth at apps/backend/src/index.ts).
 * The replay-side union (`apps/web/src/net/replay.ts`) intentionally
 * retains a `'swap'` arm to render legacy completed-match replays — read-
 * path only.
 */
export type PhaseASelection =
  | { kind: 'word'; word: string; path: ReadonlyArray<number> }
  | { kind: 'skip' }
  | null

/** A staged chess MOVE (from cell → to cell). Both indices are real board
 * cells — there is no half-staged sentinel: a move is staged only on a
 * completed drag and cleared (back to `null`) on cancel. */
export interface StagedChessMove {
  readonly from: number
  readonly to: number
}

/** A staged EMPOWER — assign a role to one of the caller's own pieces. Carries
 * `source_word_length` because the backend's empower validator derives the role
 * cost from it (chess.ts). Mutually exclusive with `extend` (D2). */
export interface StagedChessEmpower {
  readonly cell_idx: number
  readonly role: RoleKind
  readonly source_word_length: number
}

/** A staged re-empower / EXTEND — refresh an already-empowered piece's role
 * (flat 1 token, word-independent). Mutually exclusive with `empower` (D2). */
export interface StagedChessExtend {
  readonly cell_idx: number
}

/** A staged POWER-UP activation. `payload` is `Record<string, unknown>` to
 * match the backend's free-form per-power-up payload (each power-up's
 * `applyPowerUp` validates its own shape). */
export interface StagedChessPowerup {
  readonly name: PowerUpName
  readonly payload: Record<string, unknown>
}

/**
 * Phase B COMPOSITE chess step (ARCA-619 / ARCA-715). Each slot is `null` until
 * the player stages that sub-action. The composite is ALWAYS present (never
 * `null`) — an all-null composite is the do-nothing step that replaces the
 * legacy Pass (D4). Limits mirror the backend: at most one move, one role
 * action (empower XOR extend — D2), one power-up. The slots map 1:1 onto the
 * backend `CompositeChessStep` wire body (see net/turn-submit.ts
 * `toChessStepBody`); the server applies them in canonical order powerup →
 * empower/extend → move (D3) regardless of staging order.
 */
export interface PhaseBComposite {
  readonly move: StagedChessMove | null
  readonly empower: StagedChessEmpower | null
  readonly extend: StagedChessExtend | null
  readonly powerup: StagedChessPowerup | null
}

export interface TurnBuilderState {
  readonly phaseA: PhaseASelection
  readonly phaseB: PhaseBComposite
}

/**
 * Frozen empty Phase B composite — the do-nothing chess step. `clearPhaseB()`
 * resets to THIS exact reference so subscribers can cheaply identity-compare.
 */
export const EMPTY_PHASE_B: PhaseBComposite = Object.freeze({
  move: null,
  empower: null,
  extend: null,
  powerup: null,
})

/**
 * Frozen empty sentinel — every clearTurnBuilder() resets to THIS exact
 * reference so subscribers can cheaply identity-compare against EMPTY.
 */
const EMPTY: TurnBuilderState = Object.freeze({
  phaseA: null,
  phaseB: EMPTY_PHASE_B,
})

export const $turnBuilder = atom<TurnBuilderState>(EMPTY)

export function setPhaseA(p: PhaseASelection): void {
  const cur = $turnBuilder.get()
  $turnBuilder.set({ phaseA: p, phaseB: cur.phaseB })
}

/** Patch one or more Phase B slots, preserving phaseA + the untouched slots. */
function updatePhaseB(patch: Partial<PhaseBComposite>): void {
  const cur = $turnBuilder.get()
  $turnBuilder.set({ phaseA: cur.phaseA, phaseB: { ...cur.phaseB, ...patch } })
}

/** Stage (or, with `null`, clear) the move slot. */
export function stageMove(move: StagedChessMove | null): void {
  updatePhaseB({ move })
}

/** Stage (or, with `null`, clear) the empower slot. D2 — staging an empower
 * clears any staged extend (empower XOR extend). */
export function stageEmpower(empower: StagedChessEmpower | null): void {
  updatePhaseB(empower === null ? { empower: null } : { empower, extend: null })
}

/** Stage (or, with `null`, clear) the extend slot. D2 — staging an extend
 * clears any staged empower (empower XOR extend). */
export function stageExtend(extend: StagedChessExtend | null): void {
  updatePhaseB(extend === null ? { extend: null } : { extend, empower: null })
}

/** Stage (or, with `null`, clear) the power-up slot. */
export function stagePowerup(powerup: StagedChessPowerup | null): void {
  updatePhaseB({ powerup })
}

export function clearTurnBuilder(): void {
  $turnBuilder.set(EMPTY)
}

/**
 * Plan 3 Task 4 (two-step turn) — clear ONLY Phase A, preserving Phase B.
 *
 * Called after the word-step (Phase A) commit succeeds so the word-trace
 * draft is wiped while the turn stays open for the chess-step (Phase B).
 * `clearTurnBuilder()` remains the full reset fired on TURN_APPLIED.
 */
export function clearPhaseA(): void {
  const cur = $turnBuilder.get()
  if (cur.phaseA === null) return
  $turnBuilder.set({ phaseA: null, phaseB: cur.phaseB })
}

/**
 * Reset the Phase B composite to the empty (do-nothing) step, preserving
 * phaseA. Used when clearing all staged chess sub-actions at once.
 */
export function clearPhaseB(): void {
  const cur = $turnBuilder.get()
  if (cur.phaseB === EMPTY_PHASE_B) return
  $turnBuilder.set({ phaseA: cur.phaseA, phaseB: EMPTY_PHASE_B })
}

/**
 * True iff Phase A has been chosen. The chess step is always submittable (an
 * empty composite is the valid do-nothing step), so completeness of the turn
 * hinges only on a deliberate Phase A choice (word or skip).
 */
export function isComplete(state: TurnBuilderState): boolean {
  return state.phaseA !== null
}

/**
 * True iff the player has staged at least one chess sub-action (move, empower,
 * extend, or power-up). An all-null composite is the do-nothing step — Submit
 * is still allowed, it just ends the turn without acting (replaces Pass).
 */
export function hasStagedChessAction(phaseB: PhaseBComposite): boolean {
  return (
    phaseB.move !== null ||
    phaseB.empower !== null ||
    phaseB.extend !== null ||
    phaseB.powerup !== null
  )
}

/**
 * Whether to seed Phase A to word mode at the start of the player's turn.
 *
 * Word is the default Phase A action (GDD §5.3 — Submit a word | Skip). The
 * WORD/SKIP toggle displays word as the default selection, and the
 * drag-trace + keyboard-trace surfaces both gate on `phaseA.kind === 'word'`,
 * so leaving phaseA `null` at turn start made the toggle *show* word while
 * tracing was silently disabled. Seeding word mode once makes the displayed
 * default true. We only seed on the caller's own composing turn, when Phase A
 * is still unchosen, and never while an Anchor placement gate is open (Phase A
 * is blocked until placement completes). A deliberate `skip` is never
 * overridden.
 */
export function shouldDefaultPhaseAToWord(
  turn: {
    yourSlot: 0 | 1
    currentPlayerSlot: 0 | 1
    requiresAnchorPlacement: boolean
  },
  phaseA: PhaseASelection,
): boolean {
  return (
    turn.currentPlayerSlot === turn.yourSlot &&
    !turn.requiresAnchorPlacement &&
    phaseA === null
  )
}
